import { useMemo, useCallback } from "react";
import { format, differenceInDays } from "date-fns";

export interface ProcessedData {
  timestamp: number;
  pH: number | null;
  ORP: number | null;
  Temperature: number | null;
  Salinity: number | null;
  Flow: number | null;
  Vsdw: number | null;
}

interface DataPointRange {
  min?: number;
  max?: number;
}

// Atualizado para se ajustar ao formato existente nos dados da API
interface ChartDataManifest {
  dataPointTypes: {
    name: string;
    rangeMin?: number | undefined;
    rangeMax?: number | undefined;
    unit?: string;
    [key: string]: any; // Para permitir propriedades adicionais
  }[];
  [key: string]: any; // Para permitir propriedades adicionais
}

interface UseChartDataProcessorParams {
  dataPoints: any[];
  manifest: ChartDataManifest;
  timeRangeValue?: number | string;
  fromTimestamp?: number;
  toTimestamp?: number;
}

interface ChartDataProcessorResult {
  processedData: ProcessedData[];
  dates: string[];
  fullDates: string[];
  getRange: (name: string) => DataPointRange;
}

/**
 * Memoized hook that processes raw data points into chart-ready format.
 * All heavy computation (filter, map, sort, date formatting) is cached
 * and only recomputed when inputs actually change.
 */
export function useChartDataProcessor({
  dataPoints,
  manifest,
  timeRangeValue,
  fromTimestamp,
  toTimestamp,
}: UseChartDataProcessorParams): ChartDataProcessorResult {
  const { processedData, dates, fullDates } = useMemo(() => {
    return processDataPoints(
      dataPoints,
      manifest,
      timeRangeValue,
      fromTimestamp,
      toTimestamp,
    );
  }, [dataPoints, manifest, timeRangeValue, fromTimestamp, toTimestamp]);

  const getRange = useCallback(
    (name: string): DataPointRange => {
      const dataType = manifest.dataPointTypes.find((dt) => dt.name === name);
      return dataType
        ? { min: dataType.rangeMin, max: dataType.rangeMax }
        : { min: undefined, max: undefined };
    },
    [manifest],
  );

  return { processedData, dates, fullDates, getRange };
}

function processDataPoints(
  dataPoints: any[],
  manifest: ChartDataManifest,
  timeRangeValue?: number | string,
  fromTimestamp?: number,
  toTimestamp?: number,
): { processedData: ProcessedData[]; dates: string[]; fullDates: string[] } {
  // Find indices for each data type in the data points array
  const timestampIndex = 0; // Timestamp is always at index 0
  const pHIndex = manifest.dataPointTypes.findIndex((dt) => dt.name === "pH");
  const orpIndex = manifest.dataPointTypes.findIndex((dt) => dt.name === "ORP");
  const tempIndex = manifest.dataPointTypes.findIndex(
    (dt) => dt.name === "Temperature",
  );
  const salinityIndex = manifest.dataPointTypes.findIndex(
    (dt) => dt.name === "Salinity",
  );
  const flowIndex = manifest.dataPointTypes.findIndex(
    (dt) => dt.name === "Flow",
  );
  const vsdwIndex = manifest.dataPointTypes.findIndex(
    (dt) => dt.name === "Vsdw",
  );

  // Process data points and extract values
  const processedData = dataPoints
    .filter(
      (point) =>
        Array.isArray(point) &&
        point.length >=
          Math.max(
            1, // At least timestamp
            pHIndex !== -1 ? pHIndex + 1 : 0,
            orpIndex !== -1 ? orpIndex + 1 : 0,
            tempIndex !== -1 ? tempIndex + 1 : 0,
            salinityIndex !== -1 ? salinityIndex + 1 : 0,
            flowIndex !== -1 ? flowIndex + 1 : 0,
            vsdwIndex !== -1 ? vsdwIndex + 1 : 0,
          ),
    )
    .map((point) => {
      const getValue = (index: number) => {
        if (index === -1) return null;
        const value = point[index];
        // -9999 is a common sentinel value indicating "no data"
        return value === -9999 || value === 0xffff ? null : value;
      };

      // Get flow value first to check if it's 0
      const flowValue = getValue(flowIndex);
      const isFlowZero = flowValue === 0;

      // For all other sensors, return 0 if flow is 0, otherwise return the actual value
      return {
        timestamp: point[timestampIndex],
        pH: isFlowZero ? null : getValue(pHIndex),
        ORP: isFlowZero ? null : getValue(orpIndex),
        Temperature: isFlowZero ? null : getValue(tempIndex),
        Salinity: isFlowZero ? null : getValue(salinityIndex),
        Flow: flowValue,
        Vsdw: isFlowZero ? null : getValue(vsdwIndex),
      };
    });

  // Sort data by timestamp
  const sortedData = [...processedData].sort(
    (a, b) => a.timestamp - b.timestamp,
  );

  // Determine date format based on the time range
  const getDateFormat = () => {
    // First, try to use the actual time range if provided
    if (fromTimestamp && toTimestamp) {
      const duration = toTimestamp - fromTimestamp;
      const daysDiff = duration / (1000 * 60 * 60 * 24);

      if (daysDiff > 365) {
        return "MMM dd, yyyy"; // Jan 01, 2023 for yearly views
      } else if (daysDiff > 60) {
        return "MMM dd"; // Jan 01 for monthly views
      } else if (daysDiff > 2) {
        return "MMM dd"; // Jan 01 for multi-day views
      } else if (daysDiff > 1) {
        return "MMM dd HH:mm"; // Jan 01 14:30 for day+ views
      }
      return "HH:mm"; // 13:45 for hourly views
    }

    // Fallback: check if we have specific timeRangeValue
    if (
      !timeRangeValue ||
      timeRangeValue === "custom" ||
      timeRangeValue === "all"
    ) {
      // Get the time span of the data to determine format
      if (sortedData.length >= 2) {
        const firstDate = new Date(sortedData[0].timestamp);
        const lastDate = new Date(sortedData[sortedData.length - 1].timestamp);
        const daysDiff = differenceInDays(lastDate, firstDate);

        if (daysDiff > 365) {
          return "MMM dd, yyyy"; // Jan 01, 2023 for intervals > 1 year
        } else if (daysDiff > 60) {
          return "MMM dd"; // Jan 01 for intervals > 60 days
        } else if (daysDiff > 2) {
          return "MMM dd"; // Jan 01 for intervals > 2 days
        }
        return "HH:mm"; // 13:45 for intervals <= 2 days
      }
      return "MMM dd"; // Default format
    }

    // For numeric time ranges (in milliseconds)
    if (typeof timeRangeValue === "number") {
      const days = timeRangeValue / (24 * 60 * 60 * 1000);

      if (days >= 365) {
        return "MMM dd, yyyy"; // Jan 01, 2023 for yearly views
      } else if (days >= 60) {
        return "MMM dd"; // Jan 01 for monthly views
      } else if (days >= 2) {
        return "MMM dd"; // Jan 01 for daily views
      }
      return "HH:mm"; // 13:45 for hourly views
    }

    return "MMM dd"; // Default format
  };

  const dateFormat = getDateFormat();

  // Generate dates and fullDates based on the selected time range
  let dates: string[];
  let fullDates: string[];

  if (fromTimestamp && toTimestamp && sortedData.length > 0) {
    // When we have both time range and actual data, we need to be smart about X-axis labels
    // We want to show the data at its correct positions within the requested time range

    const duration = toTimestamp - fromTimestamp;
    const daysDiff = duration / (1000 * 60 * 60 * 24);

    // Generate a reasonable number of evenly distributed X-axis labels across the time range
    let numLabels = 10;
    if (daysDiff <= 1) {
      numLabels = 12; // Every 2 hours for day ranges
    } else if (daysDiff <= 7) {
      numLabels = Math.ceil(daysDiff); // Daily for week ranges
    } else if (daysDiff <= 30) {
      numLabels = Math.min(15, Math.ceil(daysDiff / 2)); // Every 2 days for month ranges
    } else {
      numLabels = Math.min(20, Math.ceil(daysDiff / 7)); // Weekly for longer ranges
    }

    dates = [];
    fullDates = [];

    // Generate evenly spaced labels across the entire time range
    for (let i = 0; i < numLabels; i++) {
      const timestamp = fromTimestamp + (i * duration) / (numLabels - 1);
      const date = new Date(timestamp);

      try {
        dates.push(format(date, dateFormat));
        fullDates.push(format(date, "MMM dd, yyyy HH:mm"));
      } catch (e) {
        console.error("Date formatting error:", e);
        dates.push("Invalid date");
        fullDates.push("Invalid date");
      }
    }
  } else if (fromTimestamp && toTimestamp) {
    // No data but we have a time range - generate labels for the requested range
    const duration = toTimestamp - fromTimestamp;
    const daysDiff = duration / (1000 * 60 * 60 * 24);

    // Determine number of points based on time range
    let numPoints = 10;
    if (daysDiff <= 1) {
      numPoints = 24; // Hourly for day ranges
    } else if (daysDiff <= 7) {
      numPoints = Math.max(Math.ceil(daysDiff), 7); // At least daily, max 7 for week
    } else if (daysDiff <= 30) {
      numPoints = Math.ceil(daysDiff); // Daily for month ranges
    } else {
      numPoints = Math.min(30, Math.ceil(daysDiff / 7)); // Weekly for longer ranges
    }

    dates = [];
    fullDates = [];

    for (let i = 0; i < numPoints; i++) {
      const timestamp = fromTimestamp + (i * duration) / (numPoints - 1);
      const date = new Date(timestamp);

      try {
        dates.push(format(date, dateFormat));
        fullDates.push(format(date, "MMM dd, yyyy HH:mm"));
      } catch (e) {
        console.error("Date formatting error:", e);
        dates.push("Invalid date");
        fullDates.push("Invalid date");
      }
    }
  } else {
    // Fallback to data-based dates if no time range provided
    dates = sortedData.map((point) => {
      try {
        const date = new Date(point.timestamp);
        if (isNaN(date.getTime())) {
          console.warn("Invalid timestamp:", point.timestamp);
          return "Invalid date";
        }
        return format(date, dateFormat);
      } catch (e) {
        console.error("Date formatting error:", e);
        return "Invalid date";
      }
    });

    fullDates = sortedData.map((point) => {
      try {
        const date = new Date(point.timestamp);
        if (isNaN(date.getTime())) {
          return "Invalid date";
        }
        return format(date, "MMM dd, yyyy HH:mm");
      } catch (e) {
        console.error("Date formatting error:", e);
        return "Invalid date";
      }
    });
  }

  // Function to find range values from manifest for each data type — kept
  // inside processDataPoints for backward compat but the hook uses useCallback.

  return {
    processedData: sortedData,
    dates,
    fullDates,
  };
}
